import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { CoreTracking } from "../../../../models/CoreTracking";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModalComponent from '../../../misc/ModalComponent';
import { ClearMapGoogleUserTracking, MapGoogleUserTracking } from '../../../../store/TrackingOperationAction';
import { RequestState } from '../../../../models/RequestState';
import { CoreGoogleUserTrackingConflict, CoreGoogleUserTrackingMapping } from '../../../../models/CoreTrackingOperation';
import StatusCode from '../../../../util/StatusCode';
import Loading from '../../../misc/Loading';
import { ExportGoogleUserTrackingMetadata } from "../../../../models/TrackingMetadata";
import GoogleUserTrackingOperationsValidationResult from "./GoogleUserTrackingOperationsValidationResult";
import CoreApiError from "../../../misc/CoreApiError";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../store/store";



interface MapGoogleUserTrackingWizardProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const MapGoogleUserTrackingWizard: React.FC<MapGoogleUserTrackingWizardProps> = (props) => {
    const dispatch = useDispatch();
    const metadata = JSON.parse(props.tracking.metadata) as ExportGoogleUserTrackingMetadata;
    const [wizardStep, setWizardStep] = useState<'input' | 'verify' | 'done'>('input');
    const [update, setUpdate] = useState<CoreGoogleUserTrackingMapping>({ googleId: '', primaryEmail: metadata.PrimaryEmail });
    const mapGoogleUserTracking = useSelector<RootState, RequestState<CoreGoogleUserTrackingConflict[]>>(state => state.trackingOperation.mapGoogleUserTracking)
    const initial: CoreGoogleUserTrackingMapping = {
        googleId: props.tracking.sourceSystemId,
        primaryEmail: metadata.PrimaryEmail,
    };
    const { t } = useTranslation(['schoolAdministration', 'common']);
    

    const updateGoogleId: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setUpdate({
            ...update,
            googleId: e.target.value,
        });
    }

    const updatePrimaryEmail: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setUpdate({
            ...update,
            primaryEmail: e.target.value,
        });
    }

    const submitInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        MapGoogleUserTracking(update, props.tracking.trackingId, true)(dispatch);
        setWizardStep('verify');
    }

    const confirmInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        MapGoogleUserTracking(update, props.tracking.trackingId, false)(dispatch);
        setWizardStep('done');
    }

    const close = (success: boolean) => {
        ClearMapGoogleUserTracking()(dispatch);
        setWizardStep('input');
        setUpdate({ googleId: '' });
        props.close(success);
    }

    switch (wizardStep) {
        case 'input': {
            if (isCurrentlyLifecycleManaged(props.tracking)) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><FontAwesomeIcon title={t('status.error', {ns: 'common'})} color="red" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.general.lifeCycleError', {ns: 'schoolAdministration'})}</ModalComponent>;
            }

            return <ModalComponent
                isOpen={true}
                toggleModal={() => close(false)}
                header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                update={submitInput}
                disabled={update.googleId === '' || update.primaryEmail === ''}
                confirm={t('action.validate', {ns: 'common'})}
                cancel={t('action.cancel', {ns: 'common'})}>
                <Form>
                    <FormGroup key="googleAccountId">
                        <Label for="googleAccountId">{t('trackingOperations.google.edit.accountLabel', {ns: 'schoolAdministration'})}</Label>
                        <Input
                            type="text"
                            name="googleAccountId"
                            id="googleAccountId"
                            placeholder={t('trackingOperations.general.identifierPlaceholder', {ns: 'schoolAdministration'})}
                            autoComplete="off"
                            invalid={update.googleId === ''}
                            value={update.googleId}
                            onChange={updateGoogleId} />
                        <FormFeedback>{t('trackingOperations.google.edit.accountIdFormFeedback', {ns: 'schoolAdministration'})}</FormFeedback>
                    </FormGroup>
                    <FormGroup key="googlePrimaryEmail">
                        <Label for="googlePrimaryEmail">{t('trackingOperations.google.edit.emailLabel', {ns: 'schoolAdministration'})}</Label>
                        <Input
                            type="text"
                            name="googlePrimaryEmail"
                            id="googlePrimaryEmail"
                            placeholder={t('trackingOperations.general.emailPlaceholder', {ns: 'schoolAdministration'})}
                            autoComplete="off"
                            invalid={update.primaryEmail === ''}
                            value={update.primaryEmail}
                            onChange={updatePrimaryEmail} />
                        <FormFeedback>{t('trackingOperations.google.edit.emailFormFeedback', {ns: 'schoolAdministration'})}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalComponent>;
        }
        case 'verify': {
            if (mapGoogleUserTracking.code === StatusCode.COMPLETE) {
                if (update.googleId === initial.googleId && update.primaryEmail === initial.primaryEmail) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                        cancel={t('action.cancel', {ns: 'common'})}>{t('trackingOperations.general.verifyNoChanges', {ns: 'schoolAdministration'})}</ModalComponent>
                } else if (mapGoogleUserTracking.data.length === 0) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                        update={confirmInput}
                        confirm={t('action.confirm', {ns: 'common'})}
                        cancel={t('action.cancel', {ns: 'common'})}>
                        <p><FontAwesomeIcon title={t('status.OK', {ns: 'common'})} color="green" icon={['fas', 'check-circle']} />{t('trackingOperations.general.noErrorsConfirm', {ns: 'schoolAdministration'})}</p>
                        {(update.googleId !== initial.googleId) && <div className="mt-3">
                            <b>{t('trackingOperations.google.edit.changeAccountId', {ns: 'schoolAdministration'})}</b>
                            <div className="gridList" style={{ gridTemplateColumns: '1fr 7fr' }}>
                                <div>{t('trackingOperations.general.from', {ns: 'schoolAdministration'})}</div>
                                <div>{initial.googleId}</div>
                                <div>{t('trackingOperations.general.to', {ns: 'schoolAdministration'})}</div>
                                <div>{update.googleId}</div>
                            </div>
                        </div>}
                        {(update.primaryEmail !== initial.primaryEmail) && <>
                            <div className="mt-3">
                                <b>{t('trackingOperations.google.edit.changeEmail', {ns: 'schoolAdministration'})}</b>
                                <div className="gridList" style={{ gridTemplateColumns: '1fr 7fr' }}>
                                    <div>{t('trackingOperations.general.from', {ns: 'schoolAdministration'})}</div>
                                    <div>{initial.primaryEmail}</div>
                                    <div>{t('trackingOperations.general.to', {ns: 'schoolAdministration'})}</div>
                                    <div>{update.primaryEmail}</div>
                                </div>
                            </div>
                            <p className="mt-3"><FontAwesomeIcon title={t('status.warning', {ns: 'common'})} color="#fc1" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.google.edit.verifyNotTaken', {ns: 'schoolAdministration', PrimaryEmail: update.primaryEmail, SourceSystemId: update.googleId})}</p>
                        </>}
                        <p className="mt-3"><FontAwesomeIcon title={t('status.warning', {ns: 'common'})} color="#fc1" icon={['fas', 'triangle-exclamation']} />{t('trackingOperations.general.mapWarningMessage', {ns: 'schoolAdministration'})}</p>
                    </ModalComponent>
                }

                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <p><FontAwesomeIcon title={t('status.error', {ns: 'common'})} color="red" icon={['fas', 'triangle-exclamation']} />{t('trackingOperations.general.conflictsDiscovered', {ns: 'schoolAdministration'})}</p>
                    <GoogleUserTrackingOperationsValidationResult googleId={update.googleId} primaryEmail={update.primaryEmail} conflicts={mapGoogleUserTracking.data} />
                </ModalComponent>;
            } else if (mapGoogleUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={mapGoogleUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
        case 'done': {
            if (mapGoogleUserTracking.code === StatusCode.COMPLETE) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(true)}
                    header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}>{t('trackingOperations.general.changeCompleted', {ns: 'schoolAdministration'})}</ModalComponent>
            } else if (mapGoogleUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={mapGoogleUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
    }
}

const isCurrentlyLifecycleManaged = (trackig: CoreTracking) => trackig.active && !trackig.deleted && (trackig.inactivated || trackig.archived);

export default MapGoogleUserTrackingWizard;
